import React, { useMemo } from 'react';
import { PushPin, PushPinSlash, Question } from '@phosphor-icons/react';
import {
  hasAssignedTeam,
  isFixedTeamTask,
  isPreassignedTask,
  normalizeTaskTeamId
} from '../utils/taskDistributionUtils.jsx';
import TaskDistributionSearchActions from './TaskDistributionSearchActions.jsx';
import TaskDistributionSectionHeading from './TaskDistributionSectionHeading.jsx';
import TaskTypeMultiSelectDropdown from './TaskTypeMultiSelectDropdown.jsx';

const formatTaskTime = (value) => {
  if (!value) {
    return '';
  }

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return '';
  }

  return parsedDate.toLocaleTimeString('es-AR', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
};

const getTaskDuration = (task = {}) => {
  const duration = [
    task.estimatedDurationMinutes,
    task.estimatedDuration,
    task.TaskType?.estimatedDuration,
    task.taskType?.estimatedDuration
  ].find((value) => Number.isFinite(Number(value)) && Number(value) > 0);

  return duration ? `${Number(duration)} min` : '';
};

const getTaskMeta = (task = {}) => {
  const taskType = task.taskTypeName || task.TaskType?.displayName || task.taskType?.displayName;
  const address = task.client?.address || task.address;
  const time = formatTaskTime(task.scheduledDate);
  return [taskType, address, time].filter(Boolean).join(' · ') || 'Sin descripción';
};

const TaskListModeSelector = ({
  mode,
  selectedTeamTaskCount,
  remainingTaskCount,
  onChange
}) => (
  <div className="grid grid-cols-2 rounded-xl border border-line bg-surface-2 p-1">
    <button
      type="button"
      onClick={() => onChange('teams')}
      className={`rounded-lg px-3 py-2.5 text-xs font-semibold transition ${
        mode === 'teams'
          ? 'bg-amber-hover text-on-amber'
          : 'text-content-muted hover:bg-surface hover:text-content'
      }`}
    >
      Tareas de equipos
      <span className={`ml-1.5 rounded-full px-1.5 py-0.5 text-[9px] ${
        mode === 'teams' ? 'bg-surface-2/15' : 'bg-surface-2'
      }`}>
        {selectedTeamTaskCount}
      </span>
    </button>
    <button
      type="button"
      onClick={() => onChange('remaining')}
      className={`rounded-lg px-3 py-2.5 text-xs font-semibold transition ${
        mode === 'remaining'
          ? 'bg-amber-hover text-on-amber'
          : 'text-content-muted hover:bg-surface hover:text-content'
      }`}
    >
      Tareas sin equipos
      <span className={`ml-1.5 rounded-full px-1.5 py-0.5 text-[9px] ${
        mode === 'remaining' ? 'bg-surface-2/15' : 'bg-surface-2'
      }`}>
        {remainingTaskCount}
      </span>
    </button>
  </div>
);

const TaskAssignmentHelp = () => (
  <div className="flex justify-end">
    <div className="group relative">
      <button
        type="button"
        aria-describedby="task-assignment-help"
        className="flex h-7 w-7 items-center justify-center rounded-full border border-line bg-surface text-content-muted transition hover:border-amber hover:text-accent-text focus:border-amber focus:text-accent-text focus:outline-none"
      >
        <Question size={15} weight="bold" />
        <span className="sr-only">Ayuda sobre tareas asignadas y fijadas</span>
      </button>
      <div
        id="task-assignment-help"
        role="tooltip"
        className="pointer-events-none absolute bottom-full right-0 z-20 mb-2 w-72 rounded-xl border border-line bg-surface-2 px-3 py-3 text-left text-[11px] leading-4 text-content opacity-0 shadow-xl transition-opacity group-hover:opacity-100 group-focus-within:opacity-100"
      >
        <p>
          <span className="font-semibold text-accent-text">Tarea pre-asignada:</span>{' '}
          ya tiene un equipo, pero puede redistribuirse.
        </p>
        <p className="mt-2">
          <span className="font-semibold text-amber-200">Tarea fijada:</span>{' '}
          conserva su equipo y no se redistribuye.
        </p>
      </div>
    </div>
  </div>
);

const TaskDistributionTasksStep = ({
  date,
  taskTypeItems,
  selectedTaskTypeIds,
  taskItems,
  selectedTeamTaskItems,
  remainingTaskItems,
  hasSelectedTeamTasks,
  isDateViewLoading,
  selectedTaskIds,
  fixedTaskIds,
  selectedTeamIds,
  teamItems,
  searchValue,
  listMode,
  onSearchChange,
  onListModeChange,
  onTaskTypesChange,
  onTasksChange,
  onToggleFixedTask,
  onRequestRemovePreassignment
}) => {
  const currentTaskItems = !hasSelectedTeamTasks
    ? taskItems
    : (listMode === 'teams' ? selectedTeamTaskItems : remainingTaskItems);
  const normalizedSearch = searchValue.trim().toLowerCase();
  const visibleTasks = useMemo(() => currentTaskItems.filter((item) => (
    !normalizedSearch
    || `${item.label} ${item.searchText || ''}`.toLowerCase().includes(normalizedSearch)
  )), [currentTaskItems, normalizedSearch]);
  const selectedTaskSet = useMemo(() => new Set(selectedTaskIds), [selectedTaskIds]);
  const selectedTeamIdSet = useMemo(
    () => new Set(selectedTeamIds.map(String)),
    [selectedTeamIds]
  );

  const toggleTask = (taskId) => {
    onTasksChange(
      selectedTaskSet.has(taskId)
        ? selectedTaskIds.filter((value) => value !== taskId)
        : [...selectedTaskIds, taskId]
    );
  };

  const selectVisibleTasks = () => {
    const nextSelectedValues = new Set(selectedTaskIds);
    visibleTasks.forEach((item) => nextSelectedValues.add(item.value));
    onTasksChange(Array.from(nextSelectedValues));
  };

  const clearCurrentTaskList = () => {
    const currentTaskIdSet = new Set(currentTaskItems.map((item) => item.value));
    onTasksChange(selectedTaskIds.filter((taskId) => !currentTaskIdSet.has(taskId)));
  };

  const changeListMode = (nextMode) => {
    onListModeChange(nextMode);
    onSearchChange('');
  };

  return (
    <div className="space-y-4">
      <TaskDistributionSectionHeading
        title="Tareas"
        description={`Pendientes para ${date}.`}
        count={`${selectedTaskIds.length}/${taskItems.length}`}
      />

      <TaskTypeMultiSelectDropdown
        items={taskTypeItems}
        selectedValues={selectedTaskTypeIds}
        onChange={onTaskTypesChange}
      />

      {hasSelectedTeamTasks ? (
        <TaskListModeSelector
          mode={listMode}
          selectedTeamTaskCount={selectedTeamTaskItems.length}
          remainingTaskCount={remainingTaskItems.length}
          onChange={changeListMode}
        />
      ) : (
        <div className="rounded-xl border border-line bg-surface-2 px-3 py-2.5 text-xs leading-5 text-content-muted">
          Los equipos seleccionados no tienen tareas asignadas. Se muestran todas las tareas disponibles.
        </div>
      )}

      <TaskDistributionSearchActions
        value={searchValue}
        onChange={onSearchChange}
        placeholder="Buscar tarea, cliente o dirección..."
        onSelectVisible={selectVisibleTasks}
        onClear={clearCurrentTaskList}
      />

      <TaskAssignmentHelp />

      <div className="max-h-[330px] space-y-2 overflow-y-auto pr-1">
        {!visibleTasks.length && (
          <div className="rounded-xl border border-dashed border-line bg-surface-2/60 px-3 py-5 text-sm text-content-muted">
            {isDateViewLoading
              ? 'Cargando tareas pendientes...'
              : (hasSelectedTeamTasks && listMode === 'remaining'
                ? 'No hay tareas sin equipo para mostrar.'
                : 'No hay tareas visibles para la fecha actual.')}
          </div>
        )}

        {visibleTasks.map((item) => {
          const task = item.task || {};
          const isSelected = selectedTaskSet.has(item.value);
          const isPersistedFixed = isFixedTeamTask(task);
          const isFixed = isPersistedFixed || fixedTaskIds.includes(item.value);
          const isPreassigned = isPreassignedTask(task);
          const teamId = normalizeTaskTeamId(task.teamId ?? task.team?.id ?? task.preassignedTeamId);
          const assignedTeamName = task.team?.name
            || task.teamName
            || task.preassignedTeam?.name
            || teamItems.find((teamItem) => String(teamItem.value) === teamId)?.label
            || 'Equipo asignado';
          const teamIsSelected = !teamId || selectedTeamIdSet.has(teamId);
          const duration = getTaskDuration(task);

          return (
            <div
              key={item.value}
              className={`rounded-xl border px-3 py-3 transition ${
                isSelected
                  ? 'border-amber bg-amber-hover/10'
                  : 'border-line bg-surface-2/70'
              } ${!teamIsSelected ? 'opacity-75' : ''}`}
            >
              <label className="flex cursor-pointer items-start gap-3">
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => toggleTask(item.value)}
                  className="mt-0.5 h-4 w-4 rounded border-line bg-surface text-accent-text"
                />
                <span className="min-w-0 flex-1">
                  <span className="flex items-start justify-between gap-3">
                    <span className="min-w-0 truncate text-sm font-semibold text-content">{item.label}</span>
                    {duration && (
                      <span className="shrink-0 rounded-full border border-line bg-surface-2 px-2 py-0.5 text-[9px] font-semibold text-content">
                        {duration}
                      </span>
                    )}
                  </span>
                  <span className="mt-1 block truncate text-[11px] text-content-muted">{getTaskMeta(task)}</span>
                </span>
              </label>

              {hasAssignedTeam(task) && (
                <div className="mt-2 flex flex-wrap items-center justify-end gap-1.5">
                  {isPreassigned && (
                    <span className="rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-[9px] font-semibold text-amber-200">
                      Pre-asignada
                    </span>
                  )}
                  <button
                    type="button"
                    role="checkbox"
                    aria-checked={isFixed}
                    aria-label={`${isFixed ? 'Fijada' : 'Fijar'} a ${assignedTeamName}`}
                    title={`${isFixed ? 'Dejar de fijar' : 'Fijar'} a ${assignedTeamName}`}
                    disabled={!isSelected || isPersistedFixed}
                    onClick={() => onToggleFixedTask(item.value)}
                    className={`inline-flex items-center gap-1.5 rounded-lg border px-2 py-1 text-[10px] font-semibold transition disabled:cursor-not-allowed disabled:opacity-60 ${
                      isFixed
                        ? 'border-amber-500/60 bg-amber-500/15 text-amber-100'
                        : 'border-line bg-surface text-content-muted hover:border-amber-500 hover:text-amber-100'
                    }`}
                  >
                    <span className={`flex h-4 w-4 items-center justify-center rounded border ${
                      isFixed
                        ? 'border-amber-400 bg-amber-400 text-on-amber'
                        : 'border-line bg-surface-2 text-content-muted'
                    }`}>
                      {isFixed
                        ? <PushPin size={11} weight="fill" />
                        : <PushPinSlash size={11} weight="bold" />}
                    </span>
                    {isFixed ? `Fijada a ${assignedTeamName}` : `Fijar a ${assignedTeamName}`}
                  </button>
                  {isPreassigned && (
                    <button
                      type="button"
                      onClick={() => onRequestRemovePreassignment(task)}
                      className="rounded-full border border-rose-500/30 bg-rose-500/10 px-2 py-0.5 text-[9px] font-semibold text-rose-200 transition hover:border-rose-400"
                    >
                      Quitar
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TaskDistributionTasksStep;
